/**
 * LiveClassroomRoom — the actual live session wrapper.
 *
 * Video slot (iframe if meeting URL allows embedding; otherwise "Open meeting"),
 * live text chat, hand-raise tray, attendance ticker, materials list.
 * Joins/leaves are tracked server-side so attendance reports can be generated
 * even when the video itself is hosted externally on Meet/Zoom.
 *
 * Uses pre-existing /school/...live-classes endpoints PLUS the new chat and
 * hand-raise additions from routes/live_classroom.py (non-breaking).
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  ArrowLeft, Send, Hand, CheckCircle2, PlayCircle, Square, Video,
  Users, MessageCircle, ExternalLink, Loader2, LogOut, LogIn,
  Mic, MicOff, VideoOff, Monitor, MonitorOff,
  Circle, StopCircle, Download, Upload, Film, Trash2, Gauge,
} from 'lucide-react';
import { CameraTrouble } from '@/components/live/CameraTrouble';
import { CameraBroadcaster } from '@/components/live/CameraBroadcaster';
import { BroadcastMethods } from '@/components/live/BroadcastMethods';
import { LiveStream } from '@/components/live/LiveStream';
import { BroadcastInfoEditor } from '@/components/live/BroadcastInfoEditor';
import { BroadcastInfoPanel } from '@/components/live/BroadcastInfoPanel';
import { useLiveTransport } from '@/lib/liveKit';
import { ClassAttendancePanel } from '@/components/school/ClassAttendancePanel';
import { PreFlightCheck } from '@/components/live/PreFlightCheck';
import { toast } from 'sonner';
import { useAuthStore } from '@/lib/store';
import { authHeaders } from '@/lib/authToken';
import { useLiveClassRtc } from '@/lib/liveClassRtc';
import useLiveRecorder from '@/lib/useLiveRecorder';
import LiveVideoGrid from '@/components/school/LiveVideoGrid';
import RecordingItem from '@/components/school/RecordingItem';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import BroadcastDashboard from '@/components/broadcast/BroadcastDashboard';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = async (path, opts = {}) => {
  const res = await fetch(`${API_URL}/api${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', ...authHeaders(), ...(opts.headers || {}) },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `Request failed (${res.status})`);
  }
  return res.json();
};

const embeddable = (url) => {
  if (!url) return null;
  try {
    const u = new URL(url);
    if (u.hostname.includes('youtube.com') && u.searchParams.get('v')) {
      return `https://www.youtube.com/embed/${u.searchParams.get('v')}`;
    }
    if (u.hostname === 'youtu.be') {
      return `https://www.youtube.com/embed${u.pathname}`;
    }
    if (u.hostname.includes('jitsi')) {
      return url;
    }
    return null;
  } catch {
    return null;
  }
};

const fmtTime = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }); }
  catch { return iso; }
};

export default function LiveClassroomRoom() {
  const { tenantId, sessionId } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user, userType } = useAuthStore();
  const [msg, setMsg] = useState('');
  const [myHandUp, setMyHandUp] = useState(false);
  const [joined, setJoined] = useState(false);
  const [checking, setChecking] = useState(false);
  const [obsOpen, setObsOpen] = useState(false);
  const scrollRef = useRef(null);

  // Best identifier for "me" — students should pass their student_id; teachers/admins can omit
  const myStudentId = user?.linked_id || user?._id || user?.id || '';

  const { data: session, isLoading: sessLoading } = useQuery({
    queryKey: ['live-class', tenantId, sessionId],
    queryFn: () => api(`/school/tenants/${tenantId}/live-classes/${sessionId}/detail`),
    refetchInterval: 10000,
  });

  const isLive = session?.status === 'live';
  const isTeacher = userType === 'teacher' || userType === 'admin';

  const { data: chat = [] } = useQuery({
    queryKey: ['live-chat', sessionId],
    queryFn: () => api(`/school/tenants/${tenantId}/live-classes/${sessionId}/chat`),
    refetchInterval: isLive ? 3000 : 15000,
    enabled: !!session,
  });

  const { data: attendance = [] } = useQuery({
    queryKey: ['live-attendance', sessionId],
    queryFn: () => api(`/school/tenants/${tenantId}/live-classes/${sessionId}/attendance`).then((r) => r.attendance || []),
    refetchInterval: isLive ? 10000 : 30000,
    enabled: !!session,
  });

  const { data: hands = [] } = useQuery({
    queryKey: ['live-hands', sessionId],
    queryFn: () => api(`/school/tenants/${tenantId}/live-classes/${sessionId}/hand-raises`),
    refetchInterval: isLive ? 5000 : 30000,
    enabled: !!session,
  });

  const presentCount = useMemo(() => attendance.filter((a) => !a.left_at).length, [attendance]);

  // Native WebRTC mesh — only initialize when the user has joined the room
  // iter 648h — when a streaming server is configured the picture goes
  // through it, so a whole year group can watch from a phone. The classroom
  // socket stays connected either way: it carries presence, chat and the
  // register.
  const transport = useLiveTransport();
  const rtc = useLiveClassRtc({
    sessionId,
    enabled: joined && !!session && (session.status === 'live' || session.status === 'scheduled'),
    mesh: !transport.livekit,
  });
  const myName = (user?.name) || `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || 'You';

  // Host recording (Phase 3 — record to local drive or upload to platform)
  const recorder = useLiveRecorder({ tenantId, sessionId, autoUpload: true });

  // Arrived from a transcript search: open that recording at that moment.
  const jumpTo = React.useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return { rec: params.get('rec') || '', seconds: Number(params.get('t') || 0) };
  }, []);

  // Recordings list
  const { data: recordings = [] } = useQuery({
    queryKey: ['live-recordings', tenantId, sessionId],
    queryFn: () => api(`/school/tenants/${tenantId}/live-classes/${sessionId}/recordings`),
    enabled: !!session,
    refetchInterval: recorder.uploading ? 5000 : 30000,
  });

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [chat.length]);

  // Auto-leave on unmount
  useEffect(() => {
    return () => {
      if (joined && myStudentId) {
        api(`/school/tenants/${tenantId}/live-classes/${sessionId}/leave`, {
          method: 'POST', body: JSON.stringify({ student_id: myStudentId }),
        }).catch(() => {});
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [joined]);

  if (sessLoading || !session) {
    return <div className="min-h-screen grid place-items-center text-slate-500"><Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading room…</div>;
  }

  const joinRoom = async () => {
    if (!myStudentId) { toast.error('Unable to identify your user record'); return; }
    try {
      await api(`/school/tenants/${tenantId}/live-classes/${sessionId}/join`, {
        method: 'POST', body: JSON.stringify({ student_id: myStudentId }),
      });
      setJoined(true);
      toast.success('You joined the class');
      qc.invalidateQueries({ queryKey: ['live-attendance', sessionId] });
    } catch (e) { toast.error(e.message); }
  };
  const leaveRoom = async () => {
    try {
      await api(`/school/tenants/${tenantId}/live-classes/${sessionId}/leave`, {
        method: 'POST', body: JSON.stringify({ student_id: myStudentId }),
      });
      setJoined(false);
      toast.success('Left the class');
      qc.invalidateQueries({ queryKey: ['live-attendance', sessionId] });
    } catch (e) { toast.error(e.message); }
  };
  const setSessionStatus = async (status) => {
    try {
      await api(`/school/tenants/${tenantId}/live-classes/${sessionId}/status?status=${status}`, { method: 'PUT' });
      toast.success(status === 'live' ? 'Live now' : 'Session ended');
      qc.invalidateQueries({ queryKey: ['live-class', tenantId, sessionId] });
      // The lesson records itself. A student who was ill should not depend on
      // the lecturer remembering, so starting the class starts the recording
      // and ending it sends the copy to the school.
      if (status === 'live' && isTeacher && !recorder.recording) {
        try {
          await recorder.start();
          toast.success('Recording this lesson — choose this tab when asked');
        } catch (e) {
          toast.error(`${e.message || 'Recording did not start'} — the lesson is live either way`);
        }
      }
      if (status !== 'live' && recorder.recording) {
        recorder.stop();
        toast.success('Recording saved — sending it to the school');
      }
    } catch (e) { toast.error(e.message); }
  };
  const sendMsg = async () => {
    const t = msg.trim();
    if (!t) return;
    setMsg('');
    try {
      await api(`/school/tenants/${tenantId}/live-classes/${sessionId}/chat`, {
        method: 'POST', body: JSON.stringify({ text: t }),
      });
      qc.invalidateQueries({ queryKey: ['live-chat', sessionId] });
    } catch (e) { toast.error(e.message); setMsg(t); }
  };
  const toggleHand = async () => {
    const action = myHandUp ? 'lower' : 'raise';
    try {
      await api(`/school/tenants/${tenantId}/live-classes/${sessionId}/hand-raise`, {
        method: 'POST', body: JSON.stringify({ action }),
      });
      setMyHandUp(!myHandUp);
      qc.invalidateQueries({ queryKey: ['live-hands', sessionId] });
    } catch (e) { toast.error(e.message); }
  };
  const ackHand = async (studentId) => {
    try {
      await api(`/school/tenants/${tenantId}/live-classes/${sessionId}/hand-raise`, {
        method: 'POST', body: JSON.stringify({ action: 'ack', student_id: studentId }),
      });
      qc.invalidateQueries({ queryKey: ['live-hands', sessionId] });
    } catch (e) { toast.error(e.message); }
  };

  const embedUrl = embeddable(session.meeting_url);

  return (
    <div className="min-h-screen bg-slate-100 p-3 sm:p-6" data-testid="live-class-room">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(`/school/${tenantId}/live-classes`)} data-testid="room-back-btn">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1 min-w-0">
            <h1 className="text-xl sm:text-2xl font-semibold truncate flex items-center gap-2">
              <Video className="h-5 w-5 text-indigo-600" /> {session.title}
            </h1>
            <p className="text-xs text-slate-500 truncate">
              {session.subject_name || 'General'}{session.teacher_name ? ` · ${session.teacher_name}` : ''} · {session.duration_minutes}min
            </p>
          </div>
          <Badge variant="outline" className={
            isLive ? 'bg-emerald-50 text-emerald-700 border-emerald-200 animate-pulse' :
            session.status === 'completed' ? 'bg-slate-100 text-slate-600' :
            session.status === 'cancelled' ? 'bg-rose-50 text-rose-700' :
            'bg-blue-50 text-blue-700 border-blue-200'
          } data-testid="room-status-badge">
            {session.status}
          </Badge>
          {isTeacher && session.status === 'scheduled' && (
            <Button onClick={() => setSessionStatus('live')} size="sm" data-testid="room-start-btn">
              <PlayCircle className="h-4 w-4 mr-1" /> Start
            </Button>
          )}
          {isTeacher && isLive && (
            <Button onClick={() => { if (window.confirm('End this session for all?')) setSessionStatus('completed'); }} size="sm" variant="destructive" data-testid="room-end-btn">
              <Square className="h-4 w-4 mr-1" /> End
            </Button>
          )}
          {isTeacher && (
            <Button onClick={() => setObsOpen(true)} size="sm" variant="outline" data-testid="room-obs-btn">
              <Video className="h-4 w-4 mr-1" /> Broadcast via OBS
            </Button>
          )}
        </div>

        <div className="grid gap-4 lg:grid-cols-[1fr_340px]">
          {/* Left — video + materials */}
          <div className="space-y-4">
            {isTeacher && (
              <Card className="p-3" data-testid="class-broadcast-methods">
                <BroadcastMethods scope="class" ident={sessionId} />
              </Card>
            )}
            {isTeacher && (
              <BroadcastInfoEditor scope="class" ident={sessionId} label="lesson" />
            )}
            {transport.livekit && joined && (isLive || session.status === 'scheduled') ? (
              rtc.canPublish ? (
                <Card className="p-4" data-testid="class-broadcaster">
                  <CameraBroadcaster
                    scope="class"
                    ident={sessionId}
                    titleLabel="Lesson"
                    titlePlaceholder={session.title || 'This lesson'}
                    note="Relayed by the school's streaming server, so the whole year group can watch."
                    onStart={() => api(`/school/tenants/${tenantId}/live-classes/${sessionId}/broadcast`, { method: 'POST', body: JSON.stringify({ active: true }) })}
                    onStop={() => api(`/school/tenants/${tenantId}/live-classes/${sessionId}/broadcast`, { method: 'POST', body: JSON.stringify({ active: false }) })}
                  />
                </Card>
              ) : (
                <Card className="overflow-hidden" data-testid="class-livekit-stage">
                  <LiveStream scope="class" ident={sessionId}
                              title={session.title} testId="class-livekit" />
                </Card>
              )
            ) : (
            <Card className="aspect-video bg-slate-900 overflow-hidden relative" data-testid="live-video-stage">
              {joined && (isLive || session.status === 'scheduled') ? (
                <div className="w-full h-full p-2">
                  <LiveVideoGrid
                    myName={myName}
                    myRole={userType}
                    localStream={rtc.localStream}
                    screenStream={rtc.screenStream}
                    mediaState={rtc.mediaState}
                    peers={rtc.peers}
                  />
                  {!rtc.connected && (
                    <div className="absolute inset-0 grid place-items-center bg-black/50 text-white text-sm">
                      <div className="flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Connecting…</div>
                    </div>
                  )}
                </div>
              ) : embedUrl ? (
                <iframe src={embedUrl} title="Live" className="w-full h-full" allow="autoplay; encrypted-media; camera; microphone" allowFullScreen />
              ) : session.meeting_url ? (
                <div className="w-full h-full grid place-items-center text-center text-white p-6 space-y-3">
                  <div>
                    <Video className="h-10 w-10 text-white/40 mx-auto mb-2" />
                    <p className="text-sm text-white/70 mb-3">
                      This class meets on {session.meeting_label || 'an external meeting'}. Tap to join — or use the in-platform classroom below.
                    </p>
                    <a href={session.meeting_url} target="_blank" rel="noreferrer">
                      <Button variant="secondary" className="mr-2" data-testid="room-join-meeting-btn">
                        <ExternalLink className="h-4 w-4 mr-1" /> Join {session.meeting_label || 'meeting'}
                      </Button>
                    </a>
                    {session.meeting_id && (
                      <p className="text-xs text-white/50 mt-2">Meeting ID {session.meeting_id}</p>
                    )}
                  </div>
                </div>
              ) : (
                <div className="w-full h-full grid place-items-center text-center text-white/60 p-6">
                  <div>
                    <Video className="h-10 w-10 text-white/30 mx-auto mb-2" />
                    <p className="text-sm">{joined ? 'Waiting for video stream…' : 'Join the classroom to start your camera, mic and screen share.'}</p>
                  </div>
                </div>
              )}
              {isLive && (
                <div className="absolute top-3 left-3 flex items-center gap-2 text-xs text-white bg-red-600/80 px-2 py-1 rounded">
                  <span className="h-2 w-2 bg-white rounded-full animate-pulse" /> LIVE
                </div>
              )}
            </Card>
            )}

            {/* Media controls — only when joined */}
            {joined && (
              <Card className="p-3 flex items-center gap-2 flex-wrap" data-testid="media-controls">
                <Button
                  size="sm"
                  variant={rtc.mediaState.mic ? 'default' : 'outline'}
                  onClick={rtc.toggleMic}
                  data-testid="toggle-mic-btn"
                  className={rtc.mediaState.mic ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
                >
                  {rtc.mediaState.mic ? <Mic className="h-4 w-4 mr-1" /> : <MicOff className="h-4 w-4 mr-1" />}
                  {rtc.mediaState.mic ? 'Mic on' : 'Mic off'}
                </Button>
                {/* iter 648g — presenting belongs to whoever is taking the
                    lesson. The server refuses a pupil's video whatever the
                    page does; hiding the buttons just stops them trying. */}
                {rtc.canPublish && !transport.livekit && (
                  <>
                    <Button
                      size="sm"
                      variant={rtc.mediaState.camera ? 'default' : 'outline'}
                      onClick={rtc.toggleCamera}
                      data-testid="toggle-camera-btn"
                      className={rtc.mediaState.camera ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
                    >
                      {rtc.mediaState.camera ? <Video className="h-4 w-4 mr-1" /> : <VideoOff className="h-4 w-4 mr-1" />}
                      {rtc.mediaState.camera ? 'Camera on' : 'Camera off'}
                    </Button>
                    <Button
                      size="sm"
                      variant={rtc.mediaState.screen ? 'default' : 'outline'}
                      onClick={rtc.toggleScreen}
                      data-testid="toggle-screen-btn"
                      className={rtc.mediaState.screen ? 'bg-indigo-600 hover:bg-indigo-700' : ''}
                    >
                      {rtc.mediaState.screen ? <MonitorOff className="h-4 w-4 mr-1" /> : <Monitor className="h-4 w-4 mr-1" />}
                      {rtc.mediaState.screen ? 'Stop sharing' : 'Share screen'}
                    </Button>
                    <Button size="sm" variant="outline"
                            onClick={() => setChecking(true)}
                            data-testid="preflight-btn"
                            title="Check the camera, the microphone and the connection before the class sees them">
                      <Gauge className="h-4 w-4 mr-1" />Check camera and sound
                    </Button>
                  </>
                )}
                {!rtc.canPublish && joined && (
                  <span className="text-xs text-slate-500" data-testid="listener-note">
                    Your teacher presents this lesson — you can speak and use the chat.
                  </span>
                )}
                {isTeacher && (
                  recorder.recording ? (
                    <Button
                      size="sm"
                      variant="default"
                      className="bg-rose-600 hover:bg-rose-700"
                      onClick={recorder.stop}
                      data-testid="record-stop-btn"
                    >
                      <StopCircle className="h-4 w-4 mr-1" />
                      Stop · {Math.floor(recorder.seconds / 60)}:{String(recorder.seconds % 60).padStart(2, '0')}
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={async () => {
                        try { await recorder.start(); toast.success('Recording — pick a tab/window when prompted'); }
                        catch (e) { toast.error(e.message || 'Recording failed to start'); }
                      }}
                      data-testid="record-start-btn"
                      title="Record this class to your local drive or upload to the school"
                    >
                      <Circle className="h-4 w-4 mr-1 text-rose-500 fill-rose-500" /> Record
                    </Button>
                  )
                )}
                <span className="ml-auto text-xs text-slate-500">{rtc.peers.length + 1} on stream</span>
              </Card>
            )}

            {isTeacher && (
              <ClassAttendancePanel tenantId={tenantId} sessionId={sessionId}
                                    live={isLive} />
            )}

            {rtc.error && (
              <CameraTrouble error={rtc.error} steps={rtc.errorSteps} />
            )}

            <PreFlightCheck open={checking} onOpenChange={setChecking} />

            {/* Join / leave */}
            <Card className="p-3 flex items-center gap-2 flex-wrap">
              {!joined ? (
                <Button onClick={joinRoom} data-testid="join-room-btn" disabled={session.status === 'completed' || session.status === 'cancelled'}>
                  <LogIn className="h-4 w-4 mr-1" /> Join classroom
                </Button>
              ) : (
                <Button variant="outline" onClick={leaveRoom} data-testid="leave-room-btn">
                  <LogOut className="h-4 w-4 mr-1" /> Leave
                </Button>
              )}
              {!isTeacher && (
                <Button variant={myHandUp ? 'default' : 'outline'} onClick={toggleHand} disabled={!isLive} data-testid="hand-raise-btn" className={myHandUp ? 'bg-amber-500 hover:bg-amber-600' : ''}>
                  <Hand className="h-4 w-4 mr-1" /> {myHandUp ? 'Lower hand' : 'Raise hand'}
                </Button>
              )}
              <div className="ml-auto flex items-center gap-2 text-xs text-slate-500">
                <Users className="h-4 w-4" /> {presentCount} present
              </div>
            </Card>

            {session.description && (
              <Card className="p-4">
                <h3 className="text-sm font-semibold mb-1">About this class</h3>
                <p className="text-sm text-slate-600 whitespace-pre-wrap">{session.description}</p>
              </Card>
            )}

            {(session.materials || []).length > 0 && (
              <Card className="p-4">
                <h3 className="text-sm font-semibold mb-2">Materials</h3>
                <ul className="space-y-1">
                  {session.materials.map((m) => (
                    <li key={m.id || m.url}>
                      <a href={m.url} target="_blank" rel="noreferrer" className="text-sm text-indigo-600 hover:underline">
                        {m.title || m.url}
                      </a>
                    </li>
                  ))}
                </ul>
              </Card>
            )}

            {/* Recordings — Phase 3 (own-host video archive) */}
            {(recorder.lastBlob || recordings.length > 0) && (
              <Card className="p-4" data-testid="recordings-panel">
                <h3 className="text-sm font-semibold mb-2 flex items-center gap-2">
                  <Film className="h-4 w-4 text-rose-600" /> Recordings ({recordings.length})
                </h3>
                {recorder.lastBlob && (
                  <div className="mb-3 flex flex-wrap items-center gap-2 p-2 rounded-lg bg-rose-50 border border-rose-200" data-testid="recorder-last-blob">
                    <span className="text-xs text-rose-800 mr-auto">
                      Recording ready · {(recorder.lastBlob.size / 1024 / 1024).toFixed(1)} MB · {Math.floor(recorder.seconds / 60)}:{String(recorder.seconds % 60).padStart(2, '0')}
                    </span>
                    <Button size="sm" variant="outline" onClick={() => recorder.downloadLast()} data-testid="record-download-btn">
                      <Download className="h-4 w-4 mr-1" /> Download
                    </Button>
                    <Button
                      size="sm"
                      onClick={async () => {
                        try {
                          await recorder.uploadLast();
                          toast.success('Recording uploaded to school');
                          qc.invalidateQueries({ queryKey: ['live-recordings', tenantId, sessionId] });
                          qc.invalidateQueries({ queryKey: ['live-class', tenantId, sessionId] });
                        } catch (e) { toast.error(e.message); }
                      }}
                      disabled={recorder.uploading}
                      data-testid="record-upload-btn"
                    >
                      {recorder.uploading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Upload className="h-4 w-4 mr-1" />}
                      {recorder.uploading ? `Uploading ${recorder.uploadProgress}%` : 'Upload to school'}
                    </Button>
                  </div>
                )}
                {recordings.length === 0 ? (
                  <p className="text-xs text-slate-400">No recordings saved yet.</p>
                ) : (
                  <ul className="space-y-2">
                    {recordings.map((r) => (
                      <RecordingItem
                        key={r.id}
                        rec={r}
                        tenantId={tenantId}
                        sessionId={sessionId}
                        isTeacher={isTeacher}
                        openAt={jumpTo.rec === r.id ? jumpTo.seconds : null}
                        studentId={!isTeacher ? myStudentId : ''}
                        onChange={() => qc.invalidateQueries({ queryKey: ['live-recordings', tenantId, sessionId] })}
                      />
                    ))}
                  </ul>
                )}
                {/* Phase 3.c — YouTube auto-publish setup hint */}
                {isTeacher && recordings.length > 0 && (
                  <div className="mt-3 p-2 rounded border border-dashed border-slate-300 bg-slate-50 text-xs text-slate-600 flex items-center gap-2" data-testid="youtube-setup-hint">
                    <span className="bg-blue-100 text-blue-700 rounded px-1.5 py-0.5 text-[10px] font-semibold">Optional</span>
                    <span>Add YouTube API credentials in System Settings to enable one-click upload of recordings.</span>
                  </div>
                )}
              </Card>
            )}

            {/* Hand-raise queue */}
            {hands.length > 0 && isTeacher && (
              <Card className="p-4">
                <h3 className="text-sm font-semibold mb-2 flex items-center gap-2">
                  <Hand className="h-4 w-4 text-amber-600" /> Raised hands ({hands.length})
                </h3>
                <ul className="space-y-2">
                  {hands.map((h) => (
                    <li key={h.id} className="flex items-center justify-between text-sm" data-testid={`hand-raise-row-${h.id}`}>
                      <span>{h.student_name || 'Student'} <span className="text-slate-400 text-xs ml-1">{fmtTime(h.raised_at)}</span></span>
                      {h.acknowledged_at ? (
                        <Badge variant="outline" className="bg-emerald-50 text-emerald-700">Acknowledged</Badge>
                      ) : (
                        <Button size="sm" variant="outline" onClick={() => ackHand(h.student_id)}>
                          <CheckCircle2 className="h-4 w-4 mr-1" /> Acknowledge
                        </Button>
                      )}
                    </li>
                  ))}
                </ul>
              </Card>
            )}
          </div>

          {/* Right — chat + roster */}
          <div className="space-y-4">
            {!isTeacher && (
              <BroadcastInfoPanel scope="class" ident={sessionId} tone="light" live={isLive} />
            )}
            <Card className="flex flex-col h-[60vh] lg:h-[calc(100vh-180px)]">
              <div className="p-3 border-b flex items-center gap-2">
                <MessageCircle className="h-4 w-4" />
                <h3 className="text-sm font-semibold">Class chat</h3>
                <span className="ml-auto text-xs text-slate-400">{chat.length}</span>
              </div>
              <div ref={scrollRef} className="flex-1 p-3 space-y-2 overflow-auto">
                {chat.length === 0 && <p className="text-xs text-slate-400 text-center py-6">No messages yet.</p>}
                {chat.map((m) => (
                  <div key={m.id} className="text-sm" data-testid={`chat-msg-${m.id}`}>
                    <div className="flex items-baseline gap-2">
                      <span className={`font-medium ${m.sender_role === 'teacher' ? 'text-indigo-700' : m.sender_role === 'admin' ? 'text-rose-700' : 'text-slate-800'}`}>
                        {m.sender_name}
                      </span>
                      <span className="text-[10px] text-slate-400">{fmtTime(m.created_at)}</span>
                    </div>
                    <div className="text-slate-700 break-words whitespace-pre-wrap">{m.text}</div>
                  </div>
                ))}
              </div>
              <div className="p-2 border-t flex gap-2">
                <Input
                  value={msg}
                  onChange={(e) => setMsg(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') sendMsg(); }}
                  placeholder={isLive ? 'Type a message…' : 'Chat opens when class starts'}
                  disabled={!isLive}
                  data-testid="chat-input"
                />
                <Button size="icon" onClick={sendMsg} disabled={!isLive || !msg.trim()} data-testid="chat-send-btn">
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </Card>

            <Card className="p-3">
              <h3 className="text-sm font-semibold mb-2 flex items-center gap-2">
                <Users className="h-4 w-4" /> Roster
                <span className="ml-auto text-xs text-slate-400">{attendance.length}</span>
              </h3>
              <ul className="space-y-1 max-h-48 overflow-auto">
                {attendance.length === 0 && <li className="text-xs text-slate-400">Nobody has joined yet.</li>}
                {attendance.map((a, i) => (
                  <li key={a.student_id || i} className="flex items-center justify-between text-xs">
                    <span>{a.student_name || 'Student'}</span>
                    <span className={`${a.left_at ? 'text-slate-400' : 'text-emerald-600'}`}>
                      {a.left_at ? `left ${fmtTime(a.left_at)}` : `joined ${fmtTime(a.joined_at)}`}
                    </span>
                  </li>
                ))}
              </ul>
            </Card>
          </div>
        </div>
      </div>

      <Dialog open={obsOpen} onOpenChange={setObsOpen}>
        <DialogContent className="max-w-3xl" data-testid="room-obs-dialog">
          <DialogHeader>
            <DialogTitle>Broadcast this class via OBS</DialogTitle>
          </DialogHeader>
          <BroadcastDashboard tenantId={tenantId} linkModule="live_classes" linkId={sessionId} />
        </DialogContent>
      </Dialog>
    </div>
  );
}
